import { router } from '@inertiajs/react'
import DashboardLayout from '@/components/DashboardLayout'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Plus, ChevronLeft, ChevronRight, ShoppingBag } from 'lucide-react'

interface Sale {
  id: number
  entry_date: string
  description: string
  quantity: number
  amount: number
  product?: { id: number; description: string } | null
  student?: { id: number; name: string } | null
  seller?: { id: number; name: string } | null
  commission_rate?: number | null
}

interface Props {
  sales: Sale[]
  sellers: { id: number; name: string }[]
  pagination: {
    current_page: number
    total_pages: number
    total_count: number
    per_page: number
  }
  filters: { seller_id?: string }
  totals: { month: number }
  is_owner: boolean
}

const brl = (value: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
    value,
  )

export default function SalesIndex({
  sales,
  sellers,
  pagination,
  filters,
  totals,
  is_owner,
}: Props) {
  const visit = (params: Record<string, unknown>) =>
    router.get(
      '/sales',
      { ...filters, ...params },
      { preserveState: true, preserveScroll: true },
    )

  return (
    <DashboardLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Balcão</h2>
            <p className="text-sm text-muted-foreground">
              {is_owner
                ? 'Vendas de produto da escola'
                : 'Suas vendas de produto'}{' '}
              · {brl(totals.month)} neste mês
            </p>
          </div>
          <Button onClick={() => router.visit('/sales/new')}>
            <Plus className="h-4 w-4 mr-2" />
            Nova venda
          </Button>
        </div>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between gap-4">
            <CardTitle className="text-base">
              {pagination.total_count}{' '}
              {pagination.total_count === 1 ? 'venda' : 'vendas'}
            </CardTitle>
            {is_owner && sellers.length > 0 && (
              <Select
                value={filters.seller_id || 'all'}
                onValueChange={(value) =>
                  visit({ seller_id: value === 'all' ? '' : value, page: 1 })
                }
              >
                <SelectTrigger className="w-[220px]">
                  <SelectValue placeholder="Vendedor" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os vendedores</SelectItem>
                  {sellers.map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Data</TableHead>
                  <TableHead>Produto</TableHead>
                  <TableHead>Aluno</TableHead>
                  {is_owner && <TableHead>Vendedor</TableHead>}
                  <TableHead className="text-right">Valor</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sales.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={is_owner ? 5 : 4}>
                      <div className="flex flex-col items-center gap-3 py-12 text-center text-muted-foreground">
                        <ShoppingBag className="h-10 w-10 opacity-40" />
                        <p className="text-sm">
                          Nenhuma venda registrada ainda.
                        </p>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  sales.map((sale) => (
                    <TableRow key={sale.id}>
                      <TableCell className="whitespace-nowrap">
                        {new Date(
                          `${sale.entry_date}T00:00:00`,
                        ).toLocaleDateString('pt-BR')}
                      </TableCell>
                      <TableCell>
                        <div className="font-medium">
                          {sale.product?.description || sale.description}
                        </div>
                        {sale.quantity > 1 && (
                          <div className="text-xs text-muted-foreground">
                            {sale.quantity} unidades
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {sale.student?.name || 'Avulsa'}
                      </TableCell>
                      {is_owner && (
                        <TableCell className="text-sm">
                          {sale.seller?.name || (
                            <span className="text-muted-foreground">—</span>
                          )}
                          {/* A taxa que valia quando a venda foi feita, não a de hoje. */}
                          {sale.commission_rate != null && (
                            <Badge variant="outline" className="ml-2">
                              {sale.commission_rate}%
                            </Badge>
                          )}
                        </TableCell>
                      )}
                      <TableCell className="text-right font-medium">
                        {brl(sale.amount)}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>

            {pagination.total_pages > 1 && (
              <div className="flex items-center justify-between mt-6">
                <span className="text-sm text-muted-foreground">
                  Página {pagination.current_page} de {pagination.total_pages}
                </span>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={pagination.current_page === 1}
                    onClick={() => visit({ page: pagination.current_page - 1 })}
                  >
                    <ChevronLeft className="h-4 w-4" />
                  </Button>
                  <Button
                    variant="outline"
                    size="icon"
                    disabled={
                      pagination.current_page === pagination.total_pages
                    }
                    onClick={() => visit({ page: pagination.current_page + 1 })}
                  >
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  )
}
